import qs from 'qs'
import { useMutation, useQuery } from '@tanstack/react-query'
import { queryClient } from '~/common/react-query/queryClient'
import fetch from '../common/fetch'

/**
 * @typedef {{
 *   created_at: string,
 *   created_by: number,
 *   dataset: string,
 *   description: string,
 *   id: number,
 *   is_favorite: boolean,
 *   last_ran: string | null,
 *   name: string,
 *   section_name: string,
 *   share_settings: unknown | null,
 *   report_type: string,
 *   url: string
 * }} GetReportsResultItem
 *
 * @typedef { import ("~/api/common").OffsetLimitPaginationResponse<GetReportsResultItem> } GetReportsResponse
 *
 * @typedef {{
 *   id: string,
 *   path: string,
 *   field: string,
 *   name: string,
 *   custom_name?: string,
 *   type: string,
 *   description: string
 * }} FieldInfo
 */

export const customReportsApi = {
  getDatasets: () => fetch.get(`/api/custom_reports/datasets`),
  /**
   * @param dataset
   * @param joins
   * @returns {Promise<Array<JoinOption>>}
   */
  getDatasetRelations: (dataset, joins) => fetch.post(`/api/custom_reports/relations/${dataset}`, { joins }),
  getFilterOptions: (datasetId, fieldId, omittedIds = []) =>
    fetch.get(`/api/custom_reports/filter_options/${datasetId}/${fieldId}?&omittedIds=${omittedIds}`),
  getCustomReports: () => fetch.get(`/api/custom_reports/list/`),
  getSavedReports: () => fetch.get(`/api/custom_reports/saved_report/`),
  /**
   * @param id
   * @param [version]
   * @returns {Promise<SavedReport>}
   */
  getSavedReport: (id, version) => {
    const urlParams = version ? { version } : {}
    const url = `/api/custom_reports/saved_report/${id}/?${qs.stringify(urlParams)}`
    return fetch.get(url)
  },
  saveReport: request => fetch.post('/api/custom_reports/saved_report/', request),
  updateReport: request => fetch.put(`/api/custom_reports/saved_report/${request.id}/`, request),
  deleteReport: id => fetch.delete(`/api/custom_reports/saved_report/${id}/`),
  runQuery: ({ id, report_type, query, page, pageSize, ...urlParams }) =>
    fetch.post(`/api/custom_reports/run/?${qs.stringify(urlParams)}`, { id, report_type, ...query, page, pageSize }),
  runNamedQuery: (queryName, namedQueryParams, page, pageSize, urlParams = {}) =>
    fetch.post(`/api/custom_reports/named_query/${queryName}/?${qs.stringify(urlParams)}`, {
      ...namedQueryParams,
      page,
      pageSize
    }),
  runDashboardQuery: (queryName, dashboardQueryParams, page, pageSize, urlParams = {}) =>
    fetch.post(`/api/custom_reports/dashboard_query/${queryName}/?${qs.stringify(urlParams)}`, {
      ...dashboardQueryParams,
      page,
      pageSize
    }),
  getDashboardQuery: (queryName, queryParams) =>
    fetch.get(`/api/custom_reports/dashboard_query/${queryName}${queryParams ? `/?parameters=${queryParams}` : ''}`),
  updateDashboardQueryParams: bulkDashboardQueryParams =>
    fetch.patch(`/api/custom_reports/dashboard_query/`, bulkDashboardQueryParams),
  exportReport: ({ id, report_type, query }, queryParams) =>
    fetch.post(`/api/custom_reports/export?${qs.stringify(queryParams)}`, {
      ...query,
      id,
      report_type,
      pageSize: -1
    }),
  exportSpecialReport: (id, queryParams) => fetch.get(`/api/reports/${id}/export?${qs.stringify(queryParams)}`),
  getExportTaskStatus: task_id => fetch.get(`/api/background_tasks/${task_id}`),
  getReportFile: trackingId => fetch.get(`/api/custom_reports/report_file/${trackingId}/`),
  getReportFileExportStatus: trackingId => fetch.get(`/api/custom_reports/report_file/export_status/${trackingId}/`),
  getArtifact: artifactId => fetch.get(`/export_archives/reports/artifacts/${artifactId}`),
  getFilterSummary: query => fetch.post(`/api/custom_reports/filter_summary/`, query),
  getReportSchemaDatasets: urlParams =>
    fetch.get(`/api/custom_reports/report_schema/datasets/?${qs.stringify(urlParams)}`),
  getReportSchemaFields: urlParams => fetch.get(`/api/custom_reports/report_schema/fields/?${qs.stringify(urlParams)}`),
  getReportSchemaPermissions: () => fetch.get(`/api/custom_reports/report_schema/permissions/`),
  exportReportSchema: ({ schema, urlParams }) =>
    fetch.get(`/api/custom_reports/report_schema/${schema}/export/?${qs.stringify(urlParams)}`),
  getReportSettings: urlParams => fetch.get(`/api/custom_reports/settings/misc/reports/?${qs.stringify(urlParams)}`),
  getSavedReportVersionHistory: (savedReportId, queryParams = {}) =>
    fetch.get(
      `/api/custom_reports/saved_report_versions/?saved_report__id=${savedReportId}&${qs.stringify(queryParams)}`
    ),
  revertReport: (id, data) => fetch.post(`/api/custom_reports/saved_report/${id}/revert/`, data),
  saveFavorites: request => fetch.post(`/api/custom_reports/add-favorite/`, request),
  deleteFavorite: (urlParams /* { id, report_key, report_type } */) =>
    fetch.delete(`/api/custom_reports/remove-favorite/?${qs.stringify(urlParams)}`)
}

export const useReportSchemaDatasets = urlParams =>
  useQuery({
    queryKey: ['report_schema_dataset', urlParams],
    queryFn: () => customReportsApi.getReportSchemaDatasets(urlParams),
    keepPreviousData: true
  })

export const useReportSchemaFields = urlParams =>
  useQuery({
    queryKey: ['report_schema_fields', urlParams],
    queryFn: () => customReportsApi.getReportSchemaFields(urlParams),
    keepPreviousData: true
  })

export const useReportSchemaPermissionsQuery = () =>
  useQuery({
    queryKey: ['report_schema_permissions', 'permissions'],
    queryFn: customReportsApi.getReportSchemaPermissions
  })

export const useCustomReportQueryKey = 'custom-report-query'

export const useCustomReportQuery = ({ id, report_type, query, page, pageSize, versionId }, enabled = true) =>
  useQuery({
    queryKey: [useCustomReportQueryKey, { id, report_type, query, page, pageSize, versionId }],
    queryFn: () => customReportsApi.runQuery({ id, report_type, query, page, pageSize, col_format: 'ui', versionId }),
    enabled,
    keepPreviousData: true
  })

export const useNamedQuery = ({ queryName, namedQueryParams, page, pageSize, urlParams }, isReady = true) =>
  useQuery({
    queryKey: ['named-query', { queryName, namedQueryParams, page, pageSize, urlParams }],
    queryFn: () => customReportsApi.runNamedQuery(queryName, namedQueryParams, page, pageSize, urlParams),
    enabled: isReady,
    keepPreviousData: true
  })

export const reportsApi = {
  /**
   * @returns {Promise<GetReportsResponse>}
   */
  getReports: urlParams => fetch.get(`/api/reports/list/?${qs.stringify(urlParams)}`),
  exportReports: urlParams => fetch.post(`/api/reports/list/export/`, { ...urlParams }),
  exportSelectedReports: urlParams => fetch.post(`/api/reports/bulk_export`, { ...urlParams }),
  getPermissions: () => fetch.get(`/api/custom_reports/permissions`),
  getBuilderContext: (_, urlParams) => fetch.get(`/api/custom_reports/builder_context/?${qs.stringify(urlParams)}`),
  getReportHistory: urlParams => fetch.get(`/api/background_tasks/?type=reports&${qs.stringify(urlParams)}`),
  createSavedReportFromBaseReport: ({ reportName, reportDescription, baseReportId }) =>
    fetch.post(`/api/custom_reports/create_saved_report_from_base_report/`, {
      reportName,
      reportDescription,
      baseReportId
    }),
  /**
   * @param {string} reportType
   * @param {number} reportId
   * @returns {Promise<FieldInfo[]>}
   */
  getReportFieldsInfo: (reportType, reportId) => fetch.get(`/api/reports/${reportType}/field_info/${reportId}`),
  /**
   * @param {string} reportType
   * @param {number} reportId
   * @returns {Promise<{ name: string }>}
   */
  getReportName: (reportType, reportId) => fetch.get(`/api/reports/${reportType}/report_name/${reportId}`),
  getFavoriteReports: (_, urlParams) => fetch.get(`/api/custom_reports/favorite_reports/?${qs.stringify(urlParams)}`)
}

export const REPORTS_QUERY_KEY = 'reports.getReports'
export const useGetReportsQuery = (urlParams, enabled = true) =>
  useQuery({
    queryKey: [REPORTS_QUERY_KEY, urlParams],
    queryFn: () => reportsApi.getReports(urlParams),
    enabled,
    keepPreviousData: true
  })

export function useDashboardQuery(queryName, queryParams) {
  const { data, isLoading, isError, isFetching, isFetched } = useQuery({
    queryKey: ['dashboard_query', queryName],
    queryFn: () => customReportsApi.getDashboardQuery(queryName, queryParams)
  })
  return { data, isLoading, isError, isFetching, isFetched }
}

export const useGetFavoriteReportsQuery = (urlParams, { enabled = true }) =>
  useQuery({ queryKey: ['reports.getFavoriteReports', urlParams], queryFn: reportsApi.getFavoriteReports, enabled })

/**
 * @param {string | undefined} reportType
 * @param {number | undefined} reportId
 */
export const useGetReportFieldsInfoQuery = (reportType, reportId, { enabled = true } = {}) =>
  useQuery({
    queryKey: [REPORTS_QUERY_KEY, { reportType, reportId }],
    queryFn: () => reportsApi.getReportFieldsInfo(reportType, reportId),
    enabled: enabled && Boolean(reportType) && Boolean(reportId)
  })

const GET_REPORT_NAME_QUERY_KEY = 'reports/getReportName'

/**
 * @param {string | undefined} reportType
 * @param {number | undefined} reportId
 */
export const useGetReportNameQuery = (reportType, reportId, { enabled = true } = {}) =>
  useQuery({
    queryKey: [GET_REPORT_NAME_QUERY_KEY, { reportType, reportId }],
    queryFn: () => reportsApi.getReportName(reportType, reportId),
    enabled: enabled && Boolean(reportType) && Boolean(reportId),
    staleTime: Infinity
  })

export const usePermissionsQuery = () =>
  useQuery({
    queryKey: ['reports', 'permissions'],
    queryFn: reportsApi.getPermissions,
    staleTime: 1000 * 60 * 5 // Five Minutes
  })
export const useBuilderContextQuery = (report_type, id, tab, uuid, version) =>
  useQuery({
    queryKey: ['reports.builderContext', { report_type, id, tab, uuid, version }],
    queryFn: reportsApi.getBuilderContext
  })

export const reportHistoryQueryOpts = urlParams => ({
  queryKey: ['reports.reportHistory', urlParams],
  queryFn: () => reportsApi.getReportHistory(urlParams)
})

export const useReportHistoryQuery = urlParams =>
  useQuery({
    queryKey: ['reports.reportHistory', urlParams],
    queryFn: () => reportsApi.getReportHistory(urlParams)
  })

export const useCreateSavedReportFromBaseReportMutation = () =>
  useMutation({
    mutationFn: reportsApi.createSavedReportFromBaseReport,
    onSuccess: () => {
      queryClient.invalidateQueries([REPORTS_QUERY_KEY])
    }
  })
